import _ from "underscore";
import InspectionSubMenu from "./subnavigation";
import DriveView, { IDriveViewOptions } from "../../DriveView";
import TransferView from "./hos/transfer";
import HosReportView from "./hos/report";
import DvirReportLogsView from "./dvir/dvirReportLogs";
import ReviewDvirView from "./dvir/review";
import { isNoVehicle } from "../../utils/device";
import { isDriverNavigatingInspectionMode, getHomeHash } from "../../utils/uri";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import InspectionLockDialog from "./inspectionLock";
import { MobileActions } from "../../store/mobile/actions";
import { InspectionType, removePin, setNoPin, getPin } from "./inspectionHelper";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { PageLevel } from "../../store/mobile/types";
import { SecurityService } from "../../utils/security";
import pageHTML from "./main.tmpl";
import moduleTranslations from "../../modules/module-translations";
import * as logger from "../../utils/logger";

const canInspectHosFn = (securityService: SecurityService) => {
    return securityService.can("useHos") || (securityService.can("attemptHos") && isNoVehicle());
};

const canInspectDvirFn = (securityService: SecurityService) => {
    return securityService.can("viewDvir") && !isNoVehicle();
};

const getInspectionType = (securityService, type?: InspectionType) => {
    return type || (canInspectHosFn(securityService) ? InspectionType.HOS_MAIN : InspectionType.DVIR_MAIN);
};

export default class InspectionView extends DriveView<Backbone.Model> {
    private readonly template;
    private readonly type: InspectionType;
    private readonly currentUserId?: string;
    private readonly currentUser = this.app.userService.getCurrentUser();
    private readonly dvirId: string;
    private readonly isOntario: boolean;
    private inspectionDialog: InspectionLockDialog | null;
    private currentView: TransferView | HosReportView | DvirReportLogsView | ReviewDvirView | null;
    private language: string;
    private menu: InspectionSubMenu | null;
    private canViewPage = true;
    private canRenderPage = true;

    constructor (options: IDriveViewOptions<Backbone.Model>) {
        super(options, {
            activeSection: "inspection",
            activeView: getInspectionType(options.app?.securityService, options.state.type),
            pageLevel: PageLevel.ZERO
        });
        this.app = options.app;
        this.storeUnsubscribe = this.app.observeStore(storeState => [storeState.mobile.language, storeState.data.driving], () => this.storeListener());
        this.listenTo(dispatcher, DispatcherEvents.ExitInspectionMode, this.showExitDialog.bind(this));
        dispatcher.trigger(DispatcherEvents.HideToastMessage);

        this.template = _.template(pageHTML);

        // Default the Roadside Check display language to the driver's configured profile language on
        // entry, instead of always opening in English (MYG-132898). The officer can still switch to
        // English (or Canadian French) from the Transfer panel selector. Exit resets both translationService
        // and mobile.language to "en" (see showExitDialog), so this default is re-applied on every entry.
        const driverLanguage = this.app.getUserLanguage();
        if (driverLanguage && this.app.store.getState().mobile.language !== driverLanguage) {
            this.app.translationService.setLanguage(driverLanguage);
            this.app.store.dispatch(MobileActions.setLanguage(driverLanguage));
            // Pre-warm module translations so the first render does not flash English for React
            // module-translated strings (mirrors the same step in topbar.ts applyLanguage).
            void this.app.translationService.getTranslator()
                .translate(moduleTranslations)
                .catch(() => { /* fall back to cached strings */ });
        }

        this.language = this.app.store.getState().mobile.language || "en";

        let state = options.state;
        this.type = getInspectionType(this.app.securityService, state.type);
        this.dvirId = state.id;
        this.isOntario = state.isOntario || false;

        let currentUser = this.app.userService.getCurrentUser();
        this.currentUserId = currentUser ? currentUser.id : undefined;

        let canInspectHos = canInspectHosFn(this.app.securityService);
        let canInspectDvir = canInspectDvirFn(this.app.securityService);
        if (!canInspectHos && !canInspectDvir) {
            this.noClearanceRedirect();
        }

        if (canInspectHos && canInspectDvir && this.isMainView()) {
            // only need submenu if both are available and we are in one of the main views
            this.setSubmenu(this.type);
        }

        if (this.currentUser && !getPin(this.app.store, this.currentUser.id, true)) {
            this.canRenderPage = false;

            // Initial dialog should be user language, presumably it is user who reads it, not officer
            this.inspectionDialog = new InspectionLockDialog(this.app.store, this.currentUser, true, this.translator, undefined, () => {
                    this.canRenderPage = true;
                    this.render();
                }, () => this.noClearanceRedirect());
            void this.inspectionDialog.open();
        }
    }

    render () {
        if (this.canRenderPage) {
            void this.display(this.template({}), this.language).then(() => {
                this.menu && this.menu.render();

                let canInspectHos = canInspectHosFn(this.app.securityService);
                let canInspectDvir = canInspectDvirFn(this.app.securityService);
                if (this.type === InspectionType.HOS_MAIN && canInspectHos) {
                    this.setView(new TransferView({ app: this.app }));
                } else if (this.type === InspectionType.DVIR_MAIN && canInspectDvir) {
                    this.setView(new DvirReportLogsView({ app: this.app }));
                } else if (this.type === InspectionType.HOS_REPORT && canInspectHos) {
                    this.setView(new HosReportView({ app: this.app, state: { isCanadaEld: false } }));
                } else if (this.type === InspectionType.CANELD_HOS_REPORT && canInspectHos) {
                    this.setView(new HosReportView({ app: this.app, state: { isCanadaEld: true } }));
                } else if (this.type === InspectionType.DVIR_REPORT && canInspectDvir) {
                    this.setView(new ReviewDvirView({ app: this.app, state: { id: this.dvirId, isOntario: this.isOntario }}));
                } else {
                    this.canViewPage = false;
                }

                !this.canViewPage && this.noClearanceRedirect();
            });
        }
        return this;
    }

    onBeforeUnload = () => {
        return new Promise((resolve, reject) => {
            // Allow navigation without dialog if navigating around inspection mode or getting kicked out for no permissions
            if (!this.canViewPage || isDriverNavigatingInspectionMode()) {
                resolve();
            } else {
                reject(new Error("Navigation rejected"));
            }
        });
    };

    remove () {
        this.currentView && this.currentView.remove();
        this.currentView = null;
        this.menu && this.menu.remove();
        this.inspectionDialog && this.inspectionDialog.close(true, !!getPin(this.app.store, this.currentUserId, true));
        this.inspectionDialog = null;

        super.remove();
        return this;
    }

    private isMainView() {
        return this.type !== InspectionType.DVIR_REPORT && this.type !== InspectionType.HOS_REPORT && this.type !== InspectionType.CANELD_HOS_REPORT;
    }

    private switchLanguage() {
        if (this.app.store.getState().mobile.language !== this.language) {
            this.language = this.app.store.getState().mobile.language || "en";
            this.currentView && this.currentView.switchLanguage(this.language);

            this.menu && this.menu.switchLanguage(this.language);
        }
    }

    private storeListener() {
        this.switchLanguage();

        let isDriving = this.app.store.getState().data.driving;
        if (isDriving && getPin(this.app.store, this.currentUserId, true)) {
            setNoPin(this.app.store, this.currentUserId);
            this.inspectionDialog && this.inspectionDialog.close(true, true);
        }
    }

    private navigateBackToApp() {
        this.canViewPage = false;
        let previousPage = this.app.store.getState().mobile.previousPage;
        if (previousPage) {
            this.app.router.navigate(previousPage, true);
            return;
        }
        this.app.router.navigate(getHomeHash(), true);
    }

    private noClearanceRedirect() {
        // The user cannot access the page they want to be on, kick them out
        removePin(this.app.store, this.currentUserId);
        this.navigateBackToApp();
    }

    private setSubmenu(activeMenu: InspectionType) {
        this.menu = new InspectionSubMenu({
            app: this.app,
            activeMenu: activeMenu,
            language: this.language
        });
    }

    private setView(view: TransferView | HosReportView | DvirReportLogsView | ReviewDvirView) {
        this.currentView = view;
        void this.currentView.translate().then(() => {
            this.currentView && this.currentView.setElement(document.querySelector("div#currentPanel")).render();
        });
    }

    private showExitDialog() {
        this.inspectionDialog = new InspectionLockDialog(
            this.app.store,
            this.currentUser,
            false,
            this.translator,
            this.language,
            () => {
                this.app.translationService.setLanguage("en");
                this.app.store.dispatch(MobileActions.setLanguage("en"));
                this.navigateBackToApp();
            },
            () => this.inspectionDialog?.close(),
            () => {
                this.canViewPage = false;
                logger.event(logger.Event.DEBUG_LOGINVIEW_NAVIGATE, "InspectionView: User logged out from inspection mode.");
                this.app.router.navigate("login,(loggingOut:true)", true);
            }
        );
        return this.inspectionDialog.open();
    }
}